/**
 * Report rendering sub-components — extracted from Reports.js
 */

export function RoomSalesTable({ data }) {
  if (!data?.rooms) return null;
  return (
    <div className="print:text-xs">
      <div className="text-center mb-4 print:mb-2">
        <h2 className="font-heading text-lg font-bold">Room Sales Report</h2>
        <p className="text-xs text-gray-500">For {data.date_from} to {data.date_to}</p>
      </div>
      {data.rooms.map(room => (
        <div key={room.room_number} className="mb-4">
          <div className="bg-gray-100 px-3 py-1 font-bold text-sm border-b border-gray-300">
            Room {room.room_number} {room.guest_name && `— ${room.guest_name}`}
          </div>
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
              <th className="text-left py-1 px-2">Article</th><th className="text-left">Description</th>
              <th className="text-center">Date</th><th className="text-center">Qty</th>
              <th className="text-left">PMS Status</th><th className="text-right">Unit Price</th>
              <th className="text-right">Extension</th><th className="text-right">Svc Chrg</th><th className="text-right">Tax 1</th>
              <th className="text-right">Tax 2</th><th className="text-right">Tax 3</th>
              <th className="text-right">Total</th>
            </tr></thead>
            <tbody>
              {room.lines.map((l, i) => (
                <tr key={`${l.article}-${l.date}-${i}`} className="border-b border-gray-50">
                  <td className="py-1 px-2 font-mono">{l.article}</td>
                  <td>{l.description}</td>
                  <td className="text-center">{l.date}</td>
                  <td className="text-center">{l.qty}</td>
                  <td><span className={`text-[9px] font-bold uppercase ${l.pms_status === 'Posted' ? 'text-green-600' : 'text-amber-600'}`}>{l.pms_status}</span></td>
                  <td className="text-right font-mono">${l.unit_price.toFixed(2)}</td>
                  <td className="text-right font-mono">${l.extension.toFixed(2)}</td>
                  <td className="text-right font-mono">${(l.svc_charge || 0).toFixed(2)}</td>
                  <td className="text-right font-mono">${l.tax1.toFixed(2)}</td>
                  <td className="text-right font-mono">${l.tax2.toFixed(2)}</td>
                  <td className="text-right font-mono">${l.tax3.toFixed(2)}</td>
                  <td className="text-right font-mono font-semibold">${l.total_charge.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 font-bold text-xs bg-gray-50">
                <td colSpan={3} className="py-1 px-2">Room {room.room_number} Totals</td>
                <td className="text-center">{room.totals.qty}</td>
                <td></td><td></td>
                <td className="text-right font-mono">${room.totals.extension.toFixed(2)}</td>
                <td className="text-right font-mono">${(room.totals.svc_charge || 0).toFixed(2)}</td>
                <td className="text-right font-mono">${room.totals.tax1.toFixed(2)}</td>
                <td className="text-right font-mono">${room.totals.tax2.toFixed(2)}</td>
                <td className="text-right font-mono">${room.totals.tax3.toFixed(2)}</td>
                <td className="text-right font-mono">${room.totals.total_charge.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
      <div className="bg-black text-white p-3 mt-2">
        <div className="flex justify-between text-sm font-bold">
          <span>Zone Totals</span>
          <div className="flex gap-6 font-mono text-xs">
            <span>Ext: ${data.zone_totals.extension.toFixed(2)}</span>
            <span>Svc: ${(data.zone_totals.svc_charge || 0).toFixed(2)}</span>
            <span>Tax1: ${data.zone_totals.tax1.toFixed(2)}</span>
            <span>Tax2: ${data.zone_totals.tax2.toFixed(2)}</span>
            <span>Tax3: ${data.zone_totals.tax3.toFixed(2)}</span>
            <span className="text-base">Total: ${data.zone_totals.total_charge.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ArticleSalesTable({ data }) {
  if (!data?.sections) return null;
  return (
    <div className="print:text-xs">
      <div className="text-center mb-4">
        <h2 className="font-heading text-lg font-bold">Article Sales Report</h2>
        <p className="text-xs text-gray-500">For {data.date_from} to {data.date_to}</p>
      </div>
      {data.sections.map(section => (
        <div key={section.category} className="mb-4">
          <div className="bg-gray-100 px-3 py-1 font-bold text-sm border-b border-gray-300">{section.category}</div>
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
              <th className="text-left py-1 px-2">Article</th><th className="text-left">Description</th>
              <th className="text-center">Qty</th><th className="text-right">Unit Price</th>
              <th className="text-right">Svc Chrg</th><th className="text-right">Tax 1</th>
              <th className="text-right">Tax 2</th><th className="text-right">Tax 3</th>
              <th className="text-right">Total Sales</th>
            </tr></thead>
            <tbody>
              {section.items.map((item) => (
                <tr key={item.article} className="border-b border-gray-50">
                  <td className="py-1 px-2 font-mono">{item.article}</td>
                  <td>{item.description}</td>
                  <td className="text-center">{item.qty_sold}</td>
                  <td className="text-right font-mono">${item.unit_price.toFixed(2)}</td>
                  <td className="text-right font-mono">${item.service_charge.toFixed(2)}</td>
                  <td className="text-right font-mono">${item.tax1.toFixed(2)}</td>
                  <td className="text-right font-mono">${item.tax2.toFixed(2)}</td>
                  <td className="text-right font-mono">${item.tax3.toFixed(2)}</td>
                  <td className="text-right font-mono font-semibold">${item.total_sales.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 font-bold text-xs bg-gray-50">
                <td colSpan={2} className="py-1 px-2">{section.category} Totals</td>
                <td className="text-center">{section.totals.qty_sold}</td><td></td>
                <td className="text-right font-mono">${section.totals.service_charge.toFixed(2)}</td>
                <td className="text-right font-mono">${section.totals.tax1.toFixed(2)}</td>
                <td className="text-right font-mono">${section.totals.tax2.toFixed(2)}</td>
                <td className="text-right font-mono">${section.totals.tax3.toFixed(2)}</td>
                <td className="text-right font-mono">${section.totals.total_sales.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
      <div className="bg-black text-white p-3 mt-2">
        <div className="flex justify-between text-sm font-bold">
          <span>Grand Totals</span>
          <div className="flex gap-6 font-mono text-xs">
            <span>Qty: {data.grand_totals.qty_sold}</span>
            <span>Svc: ${data.grand_totals.service_charge.toFixed(2)}</span>
            <span>Tax1: ${data.grand_totals.tax1.toFixed(2)}</span>
            <span>Tax2: ${data.grand_totals.tax2.toFixed(2)}</span>
            <span>Tax3: ${data.grand_totals.tax3.toFixed(2)}</span>
            <span className="text-base">Total: ${data.grand_totals.total_sales.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function UnpostedTable({ data }) {
  if (!data?.transactions) return null;
  return (
    <div className="print:text-xs">
      <div className="text-center mb-4">
        <h2 className="font-heading text-lg font-bold">Unposted Transactions</h2>
        <p className="text-xs text-gray-500">{data.date_from} to {data.date_to} — {data.total_count} transactions, ${data.total_amount.toFixed(2)} total</p>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
          <th className="text-left py-1 px-2">Ref</th><th className="text-left">Date</th>
          <th className="text-center">Room</th><th className="text-left">Guest</th>
          <th className="text-left">Operator</th><th className="text-right">Total</th>
          <th className="text-center">Status</th><th className="text-left">PMS Error</th>
        </tr></thead>
        <tbody>
          {data.transactions.map(t => (
            <tr key={t.id || t.transaction_ref} className="border-b border-gray-50 hover:bg-gray-50">
              <td className="py-1 px-2 font-mono">{t.transaction_ref}</td>
              <td>{t.created_at?.slice(0, 10)}</td>
              <td className="text-center font-bold">{t.room_number}</td>
              <td>{t.guest_name || '—'}</td>
              <td>{t.operator_name}</td>
              <td className="text-right font-mono font-semibold">${t.grand_total?.toFixed(2)}</td>
              <td className="text-center">
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${t.status === 'local_only' ? 'badge-local-only' : 'badge-failed'}`}>{t.status?.replace('_', ' ')}</span>
              </td>
              <td className="text-xs text-red-500 max-w-[200px] truncate">{t.pms_error || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.transactions.length === 0 && (
        <p className="text-center py-8 text-gray-400">No unposted transactions for this period</p>
      )}
    </div>
  );
}

export function GratuityTable({ data }) {
  if (!data?.data) return null;
  return (
    <div className="print:text-xs">
      <div className="text-center mb-4">
        <h2 className="font-heading text-lg font-bold">Gratuity Report</h2>
        <p className="text-xs text-gray-500">{data.date_from} to {data.date_to}</p>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
          <th className="text-left py-1 px-2">Cashier</th><th className="text-center">ID</th>
          <th className="text-center">Txns</th><th className="text-center">Rooms</th>
          <th className="text-right">Subtotal</th><th className="text-right">Gratuity</th>
          <th className="text-right">Grand Total</th>
        </tr></thead>
        <tbody>
          {data.data.map((row) => (
            <tr key={row.cashier_id || row.cashier} className="border-b border-gray-50">
              <td className="py-1 px-2 font-medium">{row.cashier}</td>
              <td className="text-center font-mono">{row.cashier_id}</td>
              <td className="text-center">{row.transactions}</td>
              <td className="text-center">{row.rooms}</td>
              <td className="text-right font-mono">${row.subtotal.toFixed(2)}</td>
              <td className="text-right font-mono font-bold">${row.gratuity.toFixed(2)}</td>
              <td className="text-right font-mono">${row.grand_total.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.totals && (
        <div className="bg-black text-white p-3 mt-2">
          <div className="flex justify-between text-sm font-bold">
            <span>Totals — {data.data.length} cashier(s)</span>
            <div className="flex gap-6 font-mono text-xs">
              <span>Txns: {data.totals.transactions}</span>
              <span>Gratuity: ${data.totals.gratuity_total?.toFixed(2)}</span>
              <span className="text-base">Total: ${data.totals.grand_total?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function GenericTable({ data }) {
  if (!data?.data?.length) return <p className="text-center py-12 text-gray-400">No data for selected period</p>;
  const headers = Object.keys(data.data[0]);
  return (
    <div className="overflow-x-auto">
      <div className="text-center mb-4">
        <h2 className="font-heading text-lg font-bold">{data.report}</h2>
      </div>
      <table className="w-full table-dense">
        <thead><tr className="border-b border-gray-100">
          {headers.map(h => <th key={h} className="text-left">{h.replace(/_/g, " ")}</th>)}
        </tr></thead>
        <tbody>
          {data.data.map((row, i) => (
            <tr key={`row-${i}-${Object.values(row)[0]}`} className="border-b border-gray-50 hover:bg-gray-50">
              {headers.map(h => (
                <td key={h} className={typeof row[h] === "number" ? "text-right font-mono" : ""}>
                  {typeof row[h] === "number"
                    ? (h.includes("revenue") || h.includes("total") || h.includes("subtotal") || h.includes("posted")
                      ? `$${row[h].toFixed(2)}` : row[h])
                    : typeof row[h] === "object" ? JSON.stringify(row[h]).slice(0, 50) : String(row[h] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
